import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { PermissionCode } from "@/constants/permissions";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { Flex } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SellOrderEntity } from "./schema/sell-order.schema";
import { GetSellOrdersProps } from "@/services/sell-order.service";
import { SellOrderActions } from "@/components/common/section-components/action/SellOrderActions";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import {
  clearFiltersHandler,
  fetchSellOrdersHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";
import { SellOrderFilter } from "@/components/common/section-components/filters/SellOrderFilter";
import { NoData } from "@/components/common/section-components/NoData";
import { StandardTable } from "@/components/table/StandardTable";
import dayjs from "dayjs";
import { formatCurrency } from "@/utils/format-currency";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { SellOrderStatus } from "@/constants/entity-status";
import { sellOrderHeaderElements } from "./schema/header-elements";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { isEmpty } from "@/utils/query-string";
import { usePolling } from "@/hooks/usePolling";
import { EstadosOrdenVenta } from "@/constants/enums/status/entity-status-ids";

export const SellOrderPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
  const navigate = useNavigate();
  const [loadingSellOrders, setLoadingSellOrders] = useState<boolean>(false);

  const [sellOrders, setSellOrders] = useState<{
    listado: SellOrderEntity[];
    totalPages: number;
    totalRecords: number;
  }>({
    listado: [],
    totalPages: 1,
    totalRecords: 10,
  });

  const defaultStatusFilters = [
    EstadosOrdenVenta.EnProceso,
    EstadosOrdenVenta.Negociacion,
    EstadosOrdenVenta.EnComunicacion,
  ];

  const { handleSubmit, setValue, getValues, control } = useForm<FieldValues>({
    defaultValues: {
      estados: defaultStatusFilters,
    },
  });

  const fetchSellOrders = async (
    filters?: GetSellOrdersProps,
    showLoad = true,
  ) => {
    await fetchSellOrdersHandler({
      setLoadingSellOrders,
      filters,
      showLoad,
      setSellOrders,
      navigate,
      fetchPermissions,
    });
  };

  useEffect(() => {
    if (isActive) {
      setValue("estados", defaultStatusFilters);
      submitFiltersHandler({
        event: getValues(),
        fetchSellOrders,
        setCurrentFilters,
        setFiltersApplied,
        setOpenFilters,
      });
    }
  }, [isActive]);

  const [page, setPage] = useState(1);
  const [currentFilters, setCurrentFilters] = useState({});

  const [selection, setSelection] = useState<string[]>([]);
  const hasSelection = selection.length > 0;
  const indeterminate = hasSelection && selection.length < selection?.length;

  const clearFilters = async () => {
    setPage(1);
    clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setValue,
      submitFilters,
      setOpenFilters,
    });
  };

  const submitFilters = async (event: FieldValues) => {
    setPage(1);
    submitFiltersHandler({
      event,
      fetchSellOrders: fetchSellOrders,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
    });
  };

  const handlePageChange = (page: number) => {
    setPage(page);
    fetchSellOrders({ ...currentFilters, "P.Pagina": page });
  };

  const handleDetail = (id: any) => {
    navigate(AppRoutes.SELL_ORDER_DETAIL.replace(":id", String(id)), {
      state: { page },
    });
  };

  const getActionElements = (item: any) => (
    <SellOrderActions handleDetail={handleDetail} item={item} />
  );
  const handleAdd = () => {
    navigate(AppRoutes.SELL_ORDER_CREATION);
  };

  usePolling({
    callback: async () => {
      if (!loadingSellOrders)
        await fetchSellOrders({ ...currentFilters, "P.Pagina": page }, false);
      if (hasReadAccess) await fetchPermissions(false);
    },
    delay: 10000,
    isActive: isActive && !loadingSellOrders,
  });

  const values = useWatch({
    control,
  });

  const { userPermissions, fetchPermissions } = useRoles();

  const filtersEmpty = Object.values(values ?? {}).every((v) => isEmpty(v));

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) =>
        p.codigo === PermissionCode.GRAIN_SELL_ORDER && p.tipoPermiso === "W",
    ),
  );

  const hasReadAccess = Boolean(
    userPermissions?.some(
      (p) =>
        p.codigo === PermissionCode.GRAIN_SELL_ORDER && p.tipoPermiso === "R",
    ),
  );

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        showButtons={hasWriteAccess}
        title="Órdenes de venta"
        loading={loadingSellOrders}
        addButtonLabel="Agregar"
        handleAdd={handleAdd}
      />

      <SellOrderFilter
        clearFilters={clearFilters}
        control={control}
        handleSubmit={handleSubmit}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        disableFilters={loadingSellOrders}
        disableApply={filtersEmpty}
        filtersApplied={filtersApplied}
        maxDate={dayjs()}
      />

      <Flex flexDir="column">
        {Boolean(!loadingSellOrders && !sellOrders?.listado?.length) && (
          <NoData />
        )}
        {Boolean(!loadingSellOrders && sellOrders?.listado?.length) && (
          <>
            <StandardTable
              selection={selection}
              setSelection={setSelection}
              indeterminate={indeterminate}
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              noSelection
              items={
                sellOrders?.listado?.map((el: SellOrderEntity) => ({
                  ...el,
                  fechaCreacion: dayjs(el?.fechaCreacion).format("DD/MM/YYYY"),
                  monto: el?.sinBase
                    ? "Sin base"
                    : `$${formatCurrency(el?.monto)}`,
                  tipoFijacion: isEmpty(el?.tipoFijacion)
                    ? "-"
                    : el?.tipoFijacion,
                  toneladas: `${Number(el?.toneladas).toLocaleString("es-AR")}`,
                  precioFinal:
                    el?.precioFinal && el?.precioFinal > 0
                      ? `$${formatCurrency(el?.precioFinal)}`
                      : "-",
                  displayEstado: (
                    <RequestStatusBadge
                      status={el?.estadoDescripcion as SellOrderStatus}
                    />
                  ),
                })) ?? []
              }
              headers={sellOrderHeaderElements}
              actionElements={getActionElements}
            />
            {sellOrders?.totalPages > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={sellOrders?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
